/* Branded 1080x1080 share-card generator (pure canvas — no external assets). */

const C = {
  forest: "#1f4d38",
  forestDeep: "#173a2a",
  gold: "#c99a2e",
  sand: "#f4efe4",
  sandDim: "rgba(244,239,228,0.72)",
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "", yy = y;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = w;
      yy += lineHeight;
    } else line = test;
  }
  ctx.fillText(line, x, yy);
  return yy;
}

function drawBase(ctx, eyebrow) {
  ctx.fillStyle = C.forest;
  ctx.fillRect(0, 0, 1080, 1080);
  // subtle geometric ring
  ctx.strokeStyle = "rgba(201,154,46,0.18)";
  ctx.lineWidth = 2;
  for (let i = 0; i < 6; i++) {
    ctx.beginPath();
    ctx.arc(920, 160, 90 + i * 55, 0, Math.PI * 2);
    ctx.stroke();
  }
  // brand mark
  roundRect(ctx, 72, 72, 84, 84, 20);
  ctx.fillStyle = C.gold;
  ctx.fill();
  ctx.fillStyle = C.forestDeep;
  ctx.font = "700 46px Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("ح", 114, 132);
  ctx.textAlign = "left";
  ctx.fillStyle = C.sand;
  ctx.font = "600 34px Georgia, serif";
  ctx.fillText("Hifz with Hikmah", 180, 112);
  ctx.fillStyle = C.sandDim;
  ctx.font = "500 22px Arial";
  ctx.fillText("HIKMAHVERSES", 180, 146);
  // eyebrow
  ctx.fillStyle = C.gold;
  ctx.font = "600 26px Arial";
  ctx.fillText(eyebrow.toUpperCase(), 96, 320);
}

function drawFooter(ctx, url) {
  ctx.fillStyle = C.sandDim;
  ctx.font = "500 26px Arial";
  ctx.fillText("Understand it. Reflect on it. Live it.", 96, 940);
  roundRect(ctx, 96, 968, ctx.measureText(url).width + 60, 56, 28);
  ctx.fillStyle = "rgba(244,239,228,0.12)";
  ctx.fill();
  ctx.fillStyle = C.gold;
  ctx.font = "600 26px Arial";
  ctx.fillText(url, 124, 1004);
}

export function buildAchievementCard({ name, title, desc, url }) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  drawBase(ctx, "Achievement unlocked");

  // medal
  ctx.beginPath();
  ctx.arc(180, 460, 84, 0, Math.PI * 2);
  ctx.fillStyle = C.gold;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(180, 460, 64, 0, Math.PI * 2);
  ctx.strokeStyle = C.forestDeep;
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.fillStyle = C.forestDeep;
  ctx.font = "700 64px Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("★", 180, 484);
  ctx.textAlign = "left";

  ctx.fillStyle = C.sand;
  ctx.font = "700 72px Georgia, serif";
  const endY = wrapText(ctx, title, 310, 440, 660, 84);
  ctx.fillStyle = C.sandDim;
  ctx.font = "400 34px Arial";
  wrapText(ctx, desc, 310, endY + 60, 660, 46);

  ctx.fillStyle = C.sand;
  ctx.font = "600 40px Georgia, serif";
  wrapText(ctx, `Alhamdulillah — earned by ${name}`, 96, 760, 880, 52);

  drawFooter(ctx, url);
  return canvas;
}

export function buildPulseCard({ name, stats, url }) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  drawBase(ctx, "My week with the Qur'an");

  ctx.fillStyle = C.sand;
  ctx.font = "700 66px Georgia, serif";
  wrapText(ctx, `${name}'s Hifz Pulse`, 96, 420, 880, 76);

  const tiles = [
    [String(stats.ayahs ?? 0), "New ayahs"],
    [`${stats.activeDays ?? 0}/7`, "Active days"],
    [String(stats.reviews ?? 0), "Reviews"],
    [String(stats.streak ?? 0), "Day streak"],
  ];
  tiles.forEach(([v, label], i) => {
    const x = 96 + i * 228;
    roundRect(ctx, x, 500, 204, 190, 24);
    ctx.fillStyle = "rgba(244,239,228,0.1)";
    ctx.fill();
    ctx.fillStyle = C.gold;
    ctx.font = "700 64px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText(v, x + 102, 600);
    ctx.fillStyle = C.sandDim;
    ctx.font = "500 24px Arial";
    ctx.fillText(label, x + 102, 650);
    ctx.textAlign = "left";
  });

  ctx.fillStyle = C.sand;
  ctx.font = "600 38px Georgia, serif";
  wrapText(ctx, "Memorizing the Qur'an with understanding, in shaa Allah.", 96, 780, 880, 50);

  drawFooter(ctx, url);
  return canvas;
}


function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const TREE_LEAF_COLORS = ["#5a8a67", "#77a884", "#c99a2e", "#93b89a", "#e0c25a"];

export function buildTreeCard({ name, ayahs, pages, hours, streak, url }) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  drawBase(ctx, "My Quran Tree");

  ctx.fillStyle = C.sand;
  ctx.font = "700 60px Georgia, serif";
  wrapText(ctx, `${name}'s Quran Tree`, 96, 396, 880, 70);

  // generative tree — every ayah a leaf, every page a branch
  const rand = mulberry32(1447);
  const branchCount = Math.max(1, Math.min(Math.floor(pages) + 1, 24));
  const growth = Math.min(1, Math.min(ayahs, 30) / 30 * 0.2 + Math.min(pages, 40) / 40 * 0.8);
  const scale = 1.15;
  const baseX = 560, baseY = 700;
  const trunkH = (46 + growth * 200) * scale;

  ctx.fillStyle = "rgba(244,239,228,0.10)";
  ctx.beginPath();
  ctx.ellipse(baseX, baseY + 8, 190, 26, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "#8a6a45";
  ctx.lineCap = "round";
  ctx.lineWidth = 14 * growth + 6;
  ctx.beginPath();
  ctx.moveTo(baseX, baseY);
  ctx.lineTo(baseX, baseY - trunkH);
  ctx.stroke();

  const tips = [];
  for (let i = 0; i < branchCount; i++) {
    const t = (i + 1) / (branchCount + 1);
    const sy = baseY - trunkH * (0.35 + 0.62 * t);
    const dir = i % 2 === 0 ? 1 : -1;
    const angle = (25 + rand() * 40) * (Math.PI / 180);
    const len = ((34 + rand() * 46) * growth + 18) * scale;
    const ex = baseX + dir * len * Math.cos(angle);
    const ey = sy - len * Math.sin(angle);
    ctx.lineWidth = Math.max(2.5, (5 * (1 - t) * growth + 1) * scale);
    ctx.beginPath();
    ctx.moveTo(baseX, sy);
    ctx.quadraticCurveTo((baseX + ex) / 2, ey - 8, ex, ey);
    ctx.stroke();
    tips.push([ex, ey]);
  }

  const renderLeaves = Math.min(ayahs, 320);
  if (renderLeaves > 0) {
    const perBranch = Math.ceil(renderLeaves / tips.length);
    let placed = 0;
    for (const [ex, ey] of tips) {
      for (let j = 0; j < perBranch && placed < renderLeaves; j++, placed++) {
        const lx = ex + (rand() - 0.5) * 52;
        const ly = ey + (rand() - 0.5) * 44 - 8;
        const r = (4 + rand() * 4) * scale;
        ctx.fillStyle = TREE_LEAF_COLORS[Math.floor(rand() * TREE_LEAF_COLORS.length)];
        ctx.globalAlpha = 0.75 + rand() * 0.25;
        ctx.beginPath();
        ctx.ellipse(lx, ly, r * 0.6, r, rand() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  const tiles = [
    [String(ayahs), "Leaves (ayahs)"],
    [String(Math.floor(pages)), "Branches (pages)"],
    [String(hours), "Hours"],
    [String(streak), "Day streak"],
  ];
  tiles.forEach(([v, label], i) => {
    const x = 96 + i * 228;
    roundRect(ctx, x, 748, 204, 150, 24);
    ctx.fillStyle = "rgba(244,239,228,0.1)";
    ctx.fill();
    ctx.fillStyle = C.gold;
    ctx.font = "700 54px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText(v, x + 102, 826);
    ctx.fillStyle = C.sandDim;
    ctx.font = "500 22px Arial";
    ctx.fillText(label, x + 102, 872);
    ctx.textAlign = "left";
  });

  drawFooter(ctx, url);
  return canvas;
}

const TEACHER_BADGE_LABELS = {
  verified_identity: "Verified Identity",
  verified_qualifications: "Verified Qualifications",
  verified_ijazah: "Verified Ijazah",
  institution_verified: "Institution Verified",
  master_teacher: "Master Teacher",
};

export function buildTeacherCard({ name, country, years, categories = [], badges = [], rating, url }) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  drawBase(ctx, "Verified Quran Teacher");

  // shield emblem
  ctx.save();
  ctx.translate(180, 470);
  ctx.beginPath();
  ctx.moveTo(0, -90);
  ctx.lineTo(78, -55);
  ctx.lineTo(78, 30);
  ctx.quadraticCurveTo(78, 90, 0, 118);
  ctx.quadraticCurveTo(-78, 90, -78, 30);
  ctx.lineTo(-78, -55);
  ctx.closePath();
  ctx.fillStyle = C.gold;
  ctx.fill();
  ctx.fillStyle = C.forestDeep;
  ctx.font = "700 84px Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("✓", 0, 40);
  ctx.restore();
  ctx.textAlign = "left";

  ctx.fillStyle = C.sand;
  ctx.font = "700 64px Georgia, serif";
  const endY = wrapText(ctx, name, 310, 430, 660, 74);
  ctx.fillStyle = C.sandDim;
  ctx.font = "500 30px Arial";
  const sub = [country, years ? `${years} years teaching` : null, rating ? `${rating}★` : null].filter(Boolean).join(" · ");
  ctx.fillText(sub, 310, endY + 52);

  // badge chips
  let bx = 96, by = 680;
  ctx.font = "600 26px Arial";
  badges.forEach((b) => {
    const label = TEACHER_BADGE_LABELS[b] || b;
    const w = ctx.measureText(label).width + 76;
    if (bx + w > 984) { bx = 96; by += 66; }
    roundRect(ctx, bx, by - 38, w, 52, 26);
    ctx.fillStyle = "rgba(201,154,46,0.18)";
    ctx.fill();
    ctx.fillStyle = C.gold;
    ctx.fillText(`✓ ${label}`, bx + 28, by - 2);
    bx += w + 14;
  });

  ctx.fillStyle = C.sand;
  ctx.font = "600 32px Georgia, serif";
  wrapText(ctx, categories.slice(0, 3).join(" · ") || "Quran Educator", 96, by + 84, 880, 44);
  ctx.fillStyle = C.sandDim;
  ctx.font = "400 26px Arial";
  ctx.fillText("Verified by the Global Quran Teachers Network", 96, by + 138);

  drawFooter(ctx, url);
  return canvas;
}
